import type { DungeonEncounterKind } from "./dungeon";

export type DungeonLootKind = "healing" | "weapon" | "armor" | "protection";

function lootHash(seed: number, cellId: string, salt = ""): number {
  let hash = seed >>> 0;
  for (const char of salt ? `${cellId}:${salt}` : cellId) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0;
  return hash;
}

/** Rolled independently of reward type and saved with the visible offer. */
export function getDungeonAegisRewardAmount(seed: number, cellId: string): 10 | 15 | 20 {
  return [10, 15, 20][lootHash(seed, cellId, "aegis") % 3] as 10 | 15 | 20;
}

/** A new sigil can replenish or improve protection, but two sigils never add. */
export function renewDungeonAegis(remaining: number, award: 10 | 15 | 20): number {
  return Math.min(20, Math.max(0, Math.floor(remaining), award));
}

/** Stable for a room and its run, so a restored reward never rerolls. */
export function createDungeonLootKinds(input: {
  seed: number;
  cellId: string;
  encounterKind: DungeonEncounterKind;
  weaponBonus: number;
  armorBonus: number;
}): [DungeonLootKind, DungeonLootKind] {
  const hash = lootHash(input.seed, input.cellId);
  const weapon = input.weaponBonus < 2;
  const armor = input.armorBonus < 3;
  const primary: DungeonLootKind = weapon ? "weapon" : "protection";
  // Each encounter gives two distinct claimable rewards. A capped weapon
  // makes Aegis the primary reward; capped armor makes potion the second.
  const armorChance = input.encounterKind === "cache" ? 2 : 1;
  const secondary: DungeonLootKind = armor && hash % 6 < armorChance ? "armor" : "healing";
  return [primary, secondary];
}
